<template>
  <article class="spawn-card" :class="cardClass">
    <div class="spawn-card-main">
      <div class="spawn-sprite-column">
        <div class="spawn-sprite-wrap">
          <img
            v-if="!spriteError"
            class="spawn-sprite"
            :src="view.spriteUrl"
            :alt="view.name"
            loading="lazy"
            decoding="async"
            fetchpriority="low"
            width="88"
            height="88"
            @error="$emit('sprite-error')"
          >
          <div v-else class="spawn-sprite-fallback">
            <i class="fa fa-question"></i>
          </div>
        </div>
        <div
          class="spawn-move-trigger"
          v-b-tooltip.html.hover="{ title: moveTooltipText, delay: { show: 80, hide: 0 } }"
          @focusin="prepareMoveTooltip"
          @mouseenter="prepareMoveTooltip"
        >
          <img
            v-if="!iconErrors.fighting"
            class="spawn-type-icon"
            :src="fightingTypeIconUrl"
            alt="Moves"
            @error="helpers.markIconError('fighting')"
          >
          <span v-else class="spawn-move-text">Moves</span>
        </div>
      </div>

      <div class="spawn-card-body">
        <div class="spawn-card-heading">
          <div>
            <div class="spawn-pokemon" :class="{ 'spawn-membership-name': spawn.memberOnly }">{{ view.name }}</div>
            <div class="spawn-subtitle">#{{ spawn.dexID }}</div>
          </div>
          <div class="spawn-card-heading-actions">
            <span class="spawn-tier" :class="['tier-group-' + view.rarityGroupKey, { 'spawn-censored-value': view.censorRarity }]">
              {{ view.rarityGroupLabel }}
            </span>
            <b-button
              v-if="editMode"
              size="sm"
              variant="outline-danger"
              @click="helpers.deleteEditSpawn(spawn)"
            >
              <i class="fa fa-trash"></i>
            </b-button>
          </div>
        </div>

        <div class="spawn-meta-grid">
          <div class="spawn-meta-item" :class="{ 'spawn-meta-wide': editMode }">
            <span>Level Range</span>
            <div v-if="editMode" class="spawn-inline-level-edit">
              <b-form-input :value="view.minLevel" type="text" inputmode="numeric" size="sm" :state="view.minLevelState" @input.native="helpers.setSpawnMinLevelFromEvent(spawn, $event)"></b-form-input>
              <span>-</span>
              <b-form-input :value="view.maxLevel" type="text" inputmode="numeric" size="sm" :state="view.maxLevelState" @input.native="helpers.setSpawnMaxLevelFromEvent(spawn, $event)"></b-form-input>
              <div v-if="view.safeLevels.length" class="spawn-safe-level-actions">
                <span
                  v-if="showWarnings && view.selfHarmWarning"
                  class="spawn-safe-warning-icon"
                  v-b-tooltip.html.hover
                  :title="view.selfHarmTooltip"
                >
                  <i class="fa fa-warning"></i>
                </span>
                <b-button
                  v-for="option in view.safeLevels"
                  :key="view.key + '-safe-' + option.direction"
                  size="sm"
                  variant="outline-warning"
                  @click="helpers.applySafeLevelRange(spawn, option)"
                >
                  {{ option.label }}
                </b-button>
              </div>
            </div>
            <strong v-else class="spawn-level-line">
              {{ spawn.minLVL }} - {{ spawn.maxLVL }}
              <span
                v-if="showWarnings && repelInfo"
                class="spawn-repel-icon"
                v-b-tooltip.html.hover
                :title="repelTooltip"
              >
                <img v-if="!iconErrors.repel" :src="repelIconUrl" alt="Repel" @error="helpers.markIconError('repel')">
                <i v-else class="fa fa-shield"></i>
              </span>
            </strong>
          </div>
          <div class="spawn-meta-item">
            <span>Rarity</span>
            <template v-if="editMode && !view.censorRarity">
              <b-form-select
                :value="view.rarityTier"
                :options="view.rarityOptions"
                size="sm"
                class="bg-dark text-white border-secondary mb-1"
                @input="helpers.updateSpawnRarityFromTier(spawn, $event)"
              ></b-form-select>
            </template>
            <template v-else>
              <strong :class="{ 'spawn-censored-value': view.censorRarity }">{{ view.displayRarityTier }}</strong>
              <small :class="{ 'spawn-censored-value': view.censorRarity }">{{ view.displayRawRarity }}</small>
            </template>
          </div>
          <div class="spawn-meta-item spawn-meta-wide">
            <span>{{ editMode ? 'Item Name' : 'Item Drop' }}</span>
            <template v-if="editMode">
              <reference-lookup-input
                class="spawn-item-lookup mb-1"
                :class="{ 'spawn-item-lookup-invalid': view.itemState === false }"
                :max-results="12"
                :min-len="1"
                :options="itemLookupOptions"
                :value="view.itemInput"
                :input-delay="80"
                placeholder="Item Name"
                @input="helpers.setSpawnItemName(spawn, $event)"
                @selected="helpers.selectSpawnItemOption(spawn, $event)"
              ></reference-lookup-input>
              <b-form-input
                :value="view.itemChanceInput"
                type="number"
                step="0.1"
                min="0"
                max="100"
                size="sm"
                :disabled="!view.hasItem"
                @wheel.native="helpers.preventEditInputWheel"
                @input.native="helpers.setSpawnItemDropChanceFromEvent(spawn, $event)"
              ></b-form-input>
            </template>
            <template v-else>
              <strong>{{ view.itemName }}</strong>
              <small v-if="view.hasItem">{{ view.itemDrop }}</small>
            </template>
          </div>
        </div>

        <div class="spawn-flags">
          <label class="spawn-check mb-0">
            <input v-model="spawn.memberOnly" type="checkbox" :disabled="!editMode">
            <span>Membership</span>
          </label>
          <template v-if="water">
            <label class="spawn-check mb-0">
              <span class="spawn-fishing-icon" title="Fishing">
                <img v-if="view.rodIcon" class="spawn-item-icon" :src="view.rodIcon" :alt="view.rodLabel">
                <i v-else class="fa fa-tint"></i>
              </span>
              <input type="checkbox" disabled :checked="view.fishable">
              <span>{{ view.rodLabel }}</span>
            </label>
            <span v-if="view.fishingOnly" class="spawn-fishing-only">Fishing only</span>
          </template>
        </div>

        <div v-if="editMode && water" class="spawn-edit-grid">
          <label>
            <span>Required Rod</span>
            <b-form-select
              v-model.number="spawn.required_rod"
              :options="rodOptions"
              size="sm"
              class="bg-dark text-white border-secondary"
            ></b-form-select>
          </label>
          <label class="spawn-edit-check">
            <input :checked="spawn.fishOnly === undefined ? view.fishingOnly : spawn.fishOnly" type="checkbox" @change="helpers.setSpawnFishOnly(spawn, $event.target.checked)">
            <span>Fish-only</span>
          </label>
        </div>

        <div v-if="view.formLabels.length" class="spawn-forms">
          <span v-for="label in view.formLabels" :key="view.key + '-form-' + label" class="spawn-form-badge">
            {{ label }}
          </span>
        </div>

        <div class="spawn-time-grid">
          <div v-for="period in timeRows" :key="view.key + '-' + period.key" class="spawn-time-cell" :class="{ active: period.active }">
            <div class="spawn-time-label">{{ period.label }}</div>
            <input
              type="checkbox"
              :disabled="!editMode"
              :checked="period.active"
              @change="helpers.setSpawnTimeActive(spawn, period.key, $event.target.checked)"
            >
            <div class="spawn-time-percent" :class="{ 'spawn-censored-value': period.censored }">{{ period.probability }}</div>
          </div>
        </div>
        <div v-if="view.fishingRows.length" class="spawn-fishing-chances">
          <div v-for="row in view.fishingRows" :key="view.key + '-fish-' + row.key" class="spawn-fishing-chance-row">
            <span class="spawn-fishing-rod-label">
              <img class="spawn-item-icon" :src="row.icon" :alt="row.label">
              {{ row.label }}
            </span>
            <span v-for="chance in row.chances" :key="row.key + '-' + chance.key">
              {{ chance.label }} <span :class="{ 'spawn-censored-value': chance.censored }">{{ chance.value }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import ReferenceLookupInput from './ReferenceLookupInput.vue'

const ROD_OPTIONS = [
  { value: 0, text: 'Not fishable' },
  { value: 1, text: 'Old Rod+' },
  { value: 2, text: 'Good Rod+' },
  { value: 3, text: 'Super Rod' }
]

export default {
  name: 'SpawnCard',
  components: {
    ReferenceLookupInput
  },
  props: {
    cardClass: {
      type: [String, Object, Array],
      default: ''
    },
    editMode: {
      type: Boolean,
      default: false
    },
    fightingTypeIconUrl: {
      type: String,
      required: true
    },
    getRepelInfo: {
      type: Function,
      required: true
    },
    getRepelTooltip: {
      type: Function,
      required: true
    },
    helpers: {
      type: Object,
      required: true
    },
    iconErrors: {
      type: Object,
      required: true
    },
    itemLookupOptions: {
      type: Array,
      default: () => []
    },
    probabilityLabel: {
      type: Function,
      required: true
    },
    repelIconUrl: {
      type: String,
      required: true
    },
    showWarnings: {
      type: Boolean,
      default: false
    },
    spawn: {
      type: Object,
      required: true
    },
    spriteError: {
      type: Boolean,
      default: false
    },
    timePeriods: {
      type: Array,
      required: true
    },
    water: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      moveTooltipKey: '',
      moveTooltipText: ''
    }
  },
  computed: {
    view () {
      const h = this.helpers
      const spawn = this.spawn
      const edit = this.editMode
      const water = this.water
      const hasItem = h.hasItem(spawn)
      const selfHarmWarning = edit && this.showWarnings && h.spawnHasSelfHarmingWarning(spawn)
      const censorRarity = h.censorRarityTiers()
      return {
        key: h.spawnKey(spawn),
        name: h.pokemonName(spawn),
        spriteUrl: h.pokemonSpriteUrl(spawn.dexID),
        rarityTier: h.rarityTier(spawn),
        displayRarityTier: censorRarity ? h.censoredTierLabel() : h.rarityTier(spawn),
        rarityGroupKey: censorRarity ? 'uncommon' : h.rarityGroupKey(spawn),
        rarityGroupLabel: censorRarity ? h.censoredTierLabel() : h.rarityGroupLabel(spawn),
        rawRarity: h.rawRarity(spawn),
        displayRawRarity: censorRarity ? h.censoredRarityLabel() : h.rawRarity(spawn),
        censorRarity,
        hasItem,
        itemName: h.itemName(spawn),
        itemDrop: hasItem ? h.itemDropLabel(spawn) : '',
        itemState: edit ? h.itemInputState(spawn) : null,
        itemInput: edit ? h.itemNameInputValue(spawn) : '',
        itemChanceInput: edit ? h.spawnItemDropChanceInputValue(spawn) : '',
        minLevel: edit ? h.spawnMinLevelInputValue(spawn) : null,
        maxLevel: edit ? h.spawnMaxLevelInputValue(spawn) : null,
        minLevelState: edit ? h.spawnMinLevelInputState(spawn) : null,
        maxLevelState: edit ? h.spawnMaxLevelInputState(spawn) : null,
        rarityOptions: edit ? h.rarityTierSelectOptions(spawn) : [],
        safeLevels: edit ? h.safeLevelRangeOptions(spawn) : [],
        selfHarmWarning,
        selfHarmTooltip: selfHarmWarning ? h.selfHarmingTooltip(spawn) : '',
        fishable: water && h.isFishable(spawn),
        fishingOnly: water && h.isFishingOnly(spawn),
        rodIcon: water ? h.rodIconForSpawn(spawn) : '',
        rodLabel: water ? h.rodLabel(spawn) : '',
        formLabels: h.formLabels(spawn),
        fishingRows: water ? h.fishingChanceRows(spawn) : []
      }
    },
    repelInfo () {
      return this.getRepelInfo(this.spawn)
    },
    repelTooltip () {
      return this.getRepelTooltip(this.spawn)
    },
    rodOptions () {
      return ROD_OPTIONS
    },
    timeRows () {
      return this.timePeriods.map(period => {
        const active = this.helpers.isTimeActive(this.spawn, period.key)
        const rawProbability = this.probabilityLabel(this.spawn, period.key)
        const censored = this.helpers.shouldCensorProbabilityLabel(rawProbability)
        return {
          key: period.key,
          label: period.label,
          active,
          probability: censored ? this.helpers.censoredProbabilityLabel() : rawProbability,
          censored
        }
      })
    }
  },
  methods: {
    prepareMoveTooltip () {
      if (this.moveTooltipKey === this.view.key && this.moveTooltipText) return
      this.moveTooltipKey = this.view.key
      this.moveTooltipText = this.helpers.moveTooltip(this.spawn)
    }
  }
}
</script>
